/**
 * The Division / Group rows boxes 8 and 9 both print.
 *
 * Box 8 nests them under a branch and box 9 lists them directly, so the rows
 * themselves are shared and only the owning `section` differs: a branch id for
 * operations, the literal `'intelligence'` for box 9. Branches and divisions
 * are the incident's own, not the 203's, so the 204 sees the same rows.
 */

import type React from 'react';
import { useState } from 'react';
import { Check, Plus, Trash2, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

/** The number of Division / Group rows the printed form gives a section. */
export const ICS203_MAX_DIVISIONS = 6;

/** The part of a stored division record boxes 8 and 9 print. */
export type Ics203Division = {
  id?: string;
  name?: string;
  section?: string;
};

export type Ics203DivisionListProps = {
  divisions: readonly Ics203Division[];
  onCreate: () => void;
  onRename: (division: Ics203Division, name: string) => void;
  onDelete: (id: string) => void;
  readOnly?: boolean;
  testId: string;
};

export const Ics203DivisionList: React.FC<Ics203DivisionListProps> = ({
  divisions,
  onCreate,
  onRename,
  onDelete,
  readOnly = false,
  testId,
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  return (
    <div
      className="tw:flex tw:min-w-0 tw:flex-col tw:gap-2"
      data-testid={testId}
    >
      {divisions.map((division) => {
        const isEditing = editingId != null && editingId === division.id;
        const label = division.name?.trim() || 'this division';

        return (
          <div
            key={division.id}
            className="tw:flex tw:min-w-0 tw:items-center tw:gap-2"
          >
            <span className="tw:shrink-0 tw:text-sm tw:text-muted-foreground">
              Division / Group:
            </span>
            {isEditing ? (
              <>
                <Input
                  className="tw:min-w-0"
                  aria-label={`Name for ${label}`}
                  placeholder="Enter name..."
                  value={draft}
                  onChange={(event) => setDraft(event.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Cancel editing ${label}`}
                  onClick={() => setEditingId(null)}
                >
                  <X className="tw:size-4" />
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={`Save ${label}`}
                  onClick={() => {
                    onRename(division, draft);
                    setEditingId(null);
                  }}
                >
                  <Check className="tw:size-4" />
                </Button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="tw:min-w-0 tw:flex-1 tw:break-words tw:text-left tw:text-sm tw:underline-offset-2 tw:hover:underline tw:disabled:no-underline"
                  disabled={readOnly}
                  aria-label={`Edit ${label}`}
                  onClick={() => {
                    setEditingId(division.id ?? null);
                    setDraft(division.name ?? '');
                  }}
                >
                  {division.name || '—'}
                </button>
                {readOnly ? null : (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${label}`}
                    onClick={() => division.id && onDelete(division.id)}
                  >
                    <Trash2 className="tw:size-4" />
                  </Button>
                )}
              </>
            )}
          </div>
        );
      })}

      {readOnly ? null : (
        <div className="tw:flex tw:justify-end">
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={divisions.length >= ICS203_MAX_DIVISIONS}
            onClick={onCreate}
          >
            <Plus className="tw:size-4" />
            Add Division / Group
          </Button>
        </div>
      )}
    </div>
  );
};

export default Ics203DivisionList;
